/*
 * !!!! Warning !!!!
 *
 * This component is used for both public and non-public versions of the
 * local pros page. Changes will impact both, considerations must be made
 * with both versions of this page in mind!
 */

/**
 * @typedef {import ('./MarketplaceProps').default} Props
 * @typedef {import ('./MarketplaceState').default} State
 * @typedef {import ('./Offer').default} Offer
 * @typedef {import ('./Property').default} Property
 * @typedef {import('../shared/MarketplaceService/Vendor').default} Vendor
 * @typedef {import('../shared/MovingTask').default} MovingTask
 * @typedef {import('../shared/AuthProvider/State').default} AuthState
 */
import { AuthContext } from '../shared/AuthProvider';
import { translate } from '../App/Internationalization';
import { withHomeownerService } from '../service-container';
import { withSettings } from '../shared/SettingProvider/SettingProvider';
import { withTheme } from '../shared/ThemeProvider';
import * as taskService from '../shared/task-service';
import BaseHero from '../shared/BaseHero';
import captureError from '../utils/captureError';
import ExternalAnchor from '../shared/ExternalAnchor';
import images from '../config/local/images';
import Loading from '../shared/Loading';
import Metadata from '../shared/Metadata';
import NoContent from '../shared/NoContent';
import OfferCard from './OfferCard';
import Pagination from '../shared/Pagination';
import paths from '../config/local/paths';
import React from 'react';
import { getCategoryLabel, getCategoryValue } from './helpers';
import { occupation } from '../shared/ThemeProvider/theme-utilities';
import flags from '../shared/Flags';

/** @extends {React.Component<Props, State>} */
export class Marketplace extends React.Component {
	/** @type {AuthState} */
	context = /** @type {AuthState} */ ({});

	/** @param {Props} props */
	constructor(props) {
		super(props);

		/** @type {State} */
		this.state = {
			categories: [],
			category: '',
			currentPageNumber: 1,
			errorMessage: null,
			isGettingData: true,
			isGettingSearchResults: false,
			isPaginated: false,
			lastSearchTerm: null,
			lastSearchTermLabel: null,
			offerList: [],
			postalCodeDisabled: true,
			restricted: false,
			teamMembers: [],
			tenantOfferList: [],
			totalPages: null,
		};

		this._handleChange = this._handleChange.bind(this);
		this._handleSubmit = this._handleSubmit.bind(this);
		this.isInAppModule = this.props.match.url.startsWith(paths.app.root);
		this.searchMarketplace = this.searchMarketplace.bind(this);
		this.setCurrentPageNumber = this.setCurrentPageNumber.bind(this);
	}

	/** @type {Set<string>} */
	#groups = new Set();
	#skipNextPageFetch = false;

	/** @param {number} pageNumber */
	setCurrentPageNumber(pageNumber) {
		this.setState({ currentPageNumber: pageNumber });
	}

	/**
	 * @returns {Promise<void>}
	 */
	async componentDidMount() {
		const routeCategory = this.props.location?.state?.category;
		const normalizedRouteCategory = routeCategory
			? getCategoryValue(routeCategory) ?? routeCategory
			: '';

		if (this.isInAppModule) {
			/*
			 * Fetch user data and team in parallel. Using allSettled to ensure
			 * one failure doesn't prevent the other from completing
			 */
			const results = await Promise.allSettled([
				this.getUserData(),
				this.#getHomeownerTeam(),
			]);

			// Handle individual failures without blocking the component
			results.forEach((result) => {
				if (result.status === 'rejected') {
					captureError(result.reason);
				}
			});
		} else {
			this.setState(
				{
					category: normalizedRouteCategory,
					isGettingData: false,
					postalCodeDisabled: false,
				},
				() =>
					this.searchMarketplace(
						normalizedRouteCategory,
						'',
						this.state.currentPageNumber
					)
			);
			return;
		}

		if (normalizedRouteCategory) {
			this.setState({
				category: normalizedRouteCategory,
			});
		}
	}

	/**
	 * @param {Props} prevProps
	 * @param {State} prevState
	 * @returns {Promise<void>}
	 */
	async componentDidUpdate(prevProps, prevState) {
		if (this.state.currentPageNumber !== prevState.currentPageNumber) {
			if (this.#skipNextPageFetch) {
				this.#skipNextPageFetch = false;
				return;
			}

			const postalCode = this.isInAppModule
				? this.props.property?.selectedProperty?.address?.postalCode ??
				  ''
				: '';

			await this.searchMarketplace(
				this.state.lastSearchTerm ?? '',
				postalCode,
				this.state.currentPageNumber
			);
		}
	}

	/**
	 * @param {MovingTask | null} nextTask
	 * @returns {Promise<void>}
	 */
	async getInitialMarketplaceOffers(nextTask) {
		const postalCode =
			this.props.property.selectedProperty?.address?.postalCode ?? '';

		if (this.props.location?.state?.category) {
			const routeCategory = this.props.location.state.category;
			const normalizedCategory =
				getCategoryValue(routeCategory) ?? routeCategory;

			this.setState({ category: normalizedCategory });

			await this.searchMarketplace(normalizedCategory, postalCode);
		} else if (nextTask?.searchTerm) {
			const normalizedCategory =
				getCategoryValue(nextTask.searchTerm) ?? nextTask.searchTerm;
			this.setState({ category: normalizedCategory });

			await this.searchMarketplace(normalizedCategory, postalCode);
		} else {
			// Load all vendors without category filter on initial page load
			await this.searchMarketplace('', postalCode);
		}
	}

	/**
	 * @returns {Promise<void>}
	 */
	async getUserData() {
		/**
		 * @type {MovingTask | null}
		 */
		let nextTask = null;

		try {
			const accessToken = this.context.accessToken;
			if (!accessToken) {
				this.setState({ isGettingData: false });
				return;
			}

			nextTask = await taskService.getNextMovingTask(accessToken);

			if (!this.props.property.propertyList) {
				this.setState({
					errorMessage: translate(
						'homeowner.pages.finances.view.error'
					),
				});

				return;
			}
		} catch (error) {
			captureError(error);
		}

		this.setState({ isGettingData: false }, () =>
			this.getInitialMarketplaceOffers(nextTask)
		);
	}

	/**
	 * @returns {Promise<void>}
	 */
	async #getHomeownerTeam() {
		const teamMembers =
			await this.props.homeownerService.getHomeownerTeam();

		this.setState({
			teamMembers,
		});
	}

	/**
	 * @param {string} query
	 * @param {string} postalCode
	 * @param {number} [pageNumber]
	 * @returns {Promise<void>}
	 */
	async searchMarketplace(
		query,
		postalCode,
		pageNumber = this.state.currentPageNumber
	) {
		this.setState({ isGettingSearchResults: true });

		try {
			const slug = this.props.match?.params?.whiteLabelSlug ?? '';

			const result = await this.props.marketplaceService.getVendors(
				query,
				postalCode,
				pageNumber,
				slug
			);

			if (
				pageNumber > 1 &&
				result.vendors.length < 1 &&
				result.pages > 0
			) {
				this.setState({ currentPageNumber: 1 });
				await this.searchMarketplace(query, postalCode, 1);
				return;
			}

			/** @type {Vendor[]} */
			let tenantVendors = [];
			/** @type {string[]} */
			let tenantCategories = [];
			try {
				const tenantResult =
					await this.props.marketplaceService.getVendors(
						query,
						postalCode,
						1,
						slug,
						{
							limit: 50,
							relatedType: 'tenant',
						}
					);
				tenantCategories = tenantResult.categories;
				tenantVendors = tenantResult.vendors;
			} catch (error) {
				// Tenant strip is non-blocking for local pros listing.
				captureError(error);
			}

			const categories = Array.from(
				new Set([...(result.categories ?? []), ...tenantCategories])
			).sort();

			this.setState((prevState) => ({
				...prevState,
				categories,
				errorMessage: null,
				isGettingSearchResults: false,
				isPaginated: result.pages > 1,
				lastSearchTerm: query,
				lastSearchTermLabel: getCategoryLabel(query) ?? query,
				offerList: result.vendors,
				restricted: result.restricted,
				tenantOfferList: tenantVendors,
				totalPages: result.pages,
			}));
		} catch (error) {
			captureError(error);

			this.setState({
				errorMessage: translate('global.error'),
				isGettingData: false,
				isGettingSearchResults: false,
			});
		}
	}

	/**
	 * @protected
	 * @param {{ postalCode: string }} values
	 * @returns {Promise<void>}
	 */
	async _handleSubmit(values) {
		const propertyZipCode =
			this.props.property?.selectedProperty?.address?.postalCode;

		await this.searchMarketplace(
			this.state.category,
			propertyZipCode || values.postalCode
		);
	}

	/**
	 * @protected
	 * @param {React.ChangeEvent<HTMLSelectElement>} event
	 * @returns {void}
	 */
	_handleChange(event) {
		const postalCode =
			this.props.property?.selectedProperty?.address?.postalCode ?? '';
		const category = event?.target?.value ?? '';

		if (this.state.currentPageNumber !== 1) {
			this.#skipNextPageFetch = true;
		}

		this.setState(
			{
				category,
				currentPageNumber: 1,
			},
			() => this.searchMarketplace(category, postalCode, 1)
		);
	}

	/**
	 * @returns {JSX.Element}
	 */
	#renderNoContent() {
		if (!this.state.lastSearchTerm && !this.state.categories.length) {
			return (
				<NoContent
					message={translate(
						'global.local_pros_search.no_query.message'
					)}
					title={translate('global.local_pros_search.no_query.title')}
				/>
			);
		}

		if (!this.state.restricted) {
			return (
				<NoContent
					message={translate(
						'global.local_pros_search.no_content.message',
						this.state.lastSearchTermLabel ?? ''
					)}
					title={translate(
						'global.local_pros_search.no_content.title'
					)}
				/>
			);
		}

		const job = occupation(this.props.theme.agentOccupation);

		return (
			<NoContent
				message={translate(
					'global.local_pros_search.no_content_restricted.message',
					this.state.lastSearchTermLabel ?? '',
					job
				)}
				title={translate(
					'global.local_pros_search.no_content_restricted.title',
					job
				)}
			/>
		);
	}

	/**
	 * @returns {JSX.Element | null}
	 */
	#renderCategoryFilter() {
		if (!this.state.categories.length) {
			return null;
		}

		return (
			<section className="mt-6">
				<fieldset className="input-group max-w-xs">
					<label
						className="input-label"
						htmlFor="marketplace-category"
					>
						{translate('agent.pages.offers.view.select_category')}
					</label>
					<div className="input-stylized">
						<select
							className="input"
							id="marketplace-category"
							name="category"
							onChange={this._handleChange}
							value={this.state.category}
						>
							<option value="">
								{translate(
									'agent.pages.offers.view.all_categories'
								)}
							</option>
							{this.state.categories.map((category) => (
								<option key={category} value={category}>
									{getCategoryLabel(category) ?? category}
								</option>
							))}
						</select>
					</div>
				</fieldset>
			</section>
		);
	}

	/**
	 * @returns {JSX.Element | JSX.Element[] | null}
	 */
	#renderOffers() {
		const offerList = this.#getLocalProsOffers();

		if (offerList.length < 1) {
			if (this.#getTenantVendors().length > 0) {
				return null;
			}

			return this.#renderNoContent();
		}

		return (
			<section className="mt-6 grid gap-6 sm:grid-cols-2">
				{offerList.map((offer) => (
					<OfferCard
						description={offer.description}
						isRecommended={this.#isRecommended(
							offer.related ?? null
						)}
						key={offer.id}
						logoUrl={offer.logoUrl}
						name={offer.name}
						occupation={this.props.theme.agentOccupation}
						phoneNumber={this.#getPhoneNumber(
							offer.related ?? null,
							offer.phoneNumber ?? ''
						)}
						websiteUrl={offer.websiteUrl}
					/>
				))}
			</section>
		);
	}

	/**
	 * @returns {Vendor[]}
	 */
	#getTenantVendors() {
		return this.state.tenantOfferList.filter(
			(vendor) => !!vendor && vendor.related?.type === 'tenant'
		);
	}

	/**
	 * @returns {Vendor[]}
	 */
	#getLocalProsOffers() {
		return this.state.offerList.filter(
			(vendor) => !!vendor && vendor.related?.type !== 'tenant'
		);
	}

	/**
	 * @returns {JSX.Element | null}
	 */
	#renderTenantVendorHeadlines() {
		const tenantVendors = this.#getTenantVendors();

		if (tenantVendors.length < 1) {
			return null;
		}

		return (
			<section className="mt-6">
				<h2 className="h-ms-3 mb-4">
					{translate('global.marketplace.offers.tenant_title')}
				</h2>
				<div className="overflow-x-auto pb-2">
					<div className="flex gap-6 items-start min-w-max">
						{tenantVendors.map((vendor) => {
							const content = (
								<div className="text-center max-w-[160px]">
									<img
										alt={`The ${vendor.name} logo`}
										className="h-12 object-contain mx-auto"
										src={
											vendor.logoUrl ||
											images.marketplace.logoPlaceholder
										}
									/>
									<p className="mt-2 text-sm font-medium">
										{vendor.headline || vendor.name}
									</p>
								</div>
							);

							if (!vendor.websiteUrl) {
								return <div key={vendor.id}>{content}</div>;
							}

							return (
								<ExternalAnchor
									className="link"
									dataActivityClick="marketplace_click"
									dataActivityId={vendor.name}
									href={vendor.websiteUrl}
									key={vendor.id}
									renderSpan={false}
								>
									{content}
								</ExternalAnchor>
							);
						})}
					</div>
				</div>
				<hr className="mt-6 border-grey-100" />
			</section>
		);
	}

	/**
	 * @param {{ id: string | null, type: string } | null} related
	 * @returns {boolean}
	 */
	#isRecommended(related) {
		if (!related) {
			return false;
		}

		const isAgentRecommendation =
			related.type === 'agent' && related.id === this.props.theme.agentId;

		if (!this.isInAppModule) {
			return related.type === 'agent';
		}

		if (isAgentRecommendation) {
			return this.props.settings.isEnabled(
				flags.vendor.add,
				'homeowner_tier'
			);
		}

		return false;
	}

	/**
	 * @param {{ id: string | null, type: string } | null} related
	 * @param {string} phoneNumber
	 * @returns {string}
	 */
	#getPhoneNumber(related, phoneNumber) {
		if (this.#isRecommended(related)) {
			return this.props.theme?.agentPhoneNumber ?? phoneNumber;
		}

		return phoneNumber;
	}

	render() {
		if (this.state.errorMessage) {
			return (
				<NoContent
					message={this.state.errorMessage}
					title={translate('global.error')}
				/>
			);
		}

		if (this.state.isGettingData) {
			return <Loading />;
		}

		const metadataTitle = this.props.theme?.agentName
			? `${this.props.theme.agentName}’s Marketplace`
			: 'Marketplace';
		const contentContainerClassName = this.isInAppModule
			? ''
			: 'mx-auto w-full max-w-screen-xl px-4 sm:px-6 lg:px-8';

		return (
			<React.Fragment>
				<Metadata title={metadataTitle} />
				<section className={contentContainerClassName}>
					<BaseHero
						heroImageUrl={images.marketplace.header}
						title={translate('global.marketplace.title')}
					/>
					{this.#renderCategoryFilter()}
					<hr className="mt-7 border-grey-100" />
					{this.state.isGettingSearchResults ? (
						<Loading />
					) : (
						<React.Fragment>
							{this.#renderTenantVendorHeadlines()}
							{this.#renderOffers()}
						</React.Fragment>
					)}
					{!this.state.isGettingSearchResults &&
					this.state.isPaginated ? (
						<Pagination
							currentPageNumber={this.state.currentPageNumber}
							lastPageNumber={this.state.totalPages ?? 1}
							setCurrentPageNumber={this.setCurrentPageNumber}
						/>
					) : null}
					<footer className="mt-10">
						<p className="font-medium">
							{translate('global.local_pros_search.respa')}
						</p>
					</footer>
				</section>
			</React.Fragment>
		);
	}
}

// @ts-ignore
export default withHomeownerService(withTheme(withSettings(Marketplace)));

Marketplace.contextType = AuthContext;
